import { useContext, ComponentType } from 'react'
import styled, { useTheme } from 'styled-components'
import * as ReactShare from 'react-share'

import TouchDeviceContext from 'contexts/TouchDeviceContext'
import { useToasts } from 'components/notifications/ToastProvider'
import { Method } from '../../analytics/share'

import CopyIcon from '../icons/Copy'
import MessageIcon from '../icons/Message'
import EmailIcon from '../icons/Mail'
import MessengerIcon from '../icons/Messenger'
import { InlineInput, TextField } from '../form'

import Button from './Button'
import CopyToClipboardButton from './CopyToClipboardButton'

// react-share's types haven't been updated for the new React 18 attributes
// (placeholder on iframe, onPointerEnter/LeaveCapture). Cast to bypass — the
// runtime API is unchanged.
const EmailShareButton = ReactShare.EmailShareButton as ComponentType<any>
const FacebookShareButton = ReactShare.FacebookShareButton as ComponentType<any>
const TwitterShareButton = ReactShare.TwitterShareButton as ComponentType<any>
const WhatsappShareButton = ReactShare.WhatsappShareButton as ComponentType<any>
const FacebookMessengerShareButton =
  ReactShare.FacebookMessengerShareButton as ComponentType<any>
const FacebookIcon = ReactShare.FacebookIcon as ComponentType<any>
const TwitterIcon = ReactShare.TwitterIcon as ComponentType<any>
const WhatsappIcon = ReactShare.WhatsappIcon as ComponentType<any>

const List = styled.div`
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  & > * {
    display: flex;
    align-items: center;
    width: 100%;
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-sm']?.['font-size']?.value};

    svg {
      width: 24px;
      height: 24px;
      margin-right: 16px;
    }
  }

  button {
    display: flex;
    align-items: center;
  }

  input {
    margin: 0;
  }
`

const ShareMethodButton = styled(Button).attrs({
  ghost: true,
  uppercase: false,
})``

const ShareMethodButtonOuter = styled(ShareMethodButton).attrs({ as: 'div' })`
  padding: 0;

  & > button {
    padding: 16px !important;
    width: 100% !important;
  }
`

const CopyWrapper = styled.div`
  margin-left: 8px;

  button {
    height: 100%;
  }
`

const iconProps = {
  size: 48,
  round: true,
}

type ShareListProps = {
  url: string
  message?: string
  excludeMethods?: Method[]
  onShare?: (method: Method) => void
}

const ShareList = ({
  url,
  message,
  excludeMethods = [],
  onShare,
}: ShareListProps) => {
  const { addToast } = useToasts()
  const theme = useTheme()

  const isTouchDevice = useContext(TouchDeviceContext)

  const body = `${message}: ${url}`

  return (
    <List data-testid="share-list">
      {!excludeMethods.includes('link') && (
        <div>
          <InlineInput $margin="0">
            <TextField
              id="share_link"
              value={url}
              rounded
              readOnly
              style={{
                color: theme?.['-ds-neutral-700']?.value,
                borderColor: theme?.['-ds-neutral-500']?.value,
              }}
            />
          </InlineInput>
          <CopyWrapper>
            <CopyToClipboardButton
              dataClipboardTarget="#share_link"
              ghost
              onSuccess={() => {
                onShare('link')
                addToast('Link copied')
              }}
              style={{ marginRight: 0 }}
            >
              <CopyIcon />
              Copy Link
            </CopyToClipboardButton>
          </CopyWrapper>
        </div>
      )}

      {!excludeMethods.includes('email') && (
        <ShareMethodButtonOuter>
          <EmailShareButton
            beforeOnClick={() => onShare('email')}
            subject={message}
            url={url}
          >
            <EmailIcon />
            Email
          </EmailShareButton>
        </ShareMethodButtonOuter>
      )}

      {!excludeMethods.includes('sms') && (
        <ShareMethodButton
          as="a"
          href={`sms:?&body=${encodeURIComponent(body)}`}
          onClick={() => onShare('sms')}
        >
          <MessageIcon />
          Messages
        </ShareMethodButton>
      )}

      {!excludeMethods.includes('facebook') && (
        <ShareMethodButtonOuter>
          <FacebookShareButton
            onClick={() => onShare('facebook')}
            quote={message}
            url={url}
          >
            <FacebookIcon {...iconProps} /> Facebook
          </FacebookShareButton>
        </ShareMethodButtonOuter>
      )}

      {!excludeMethods.includes('twitter') && (
        <ShareMethodButtonOuter>
          <TwitterShareButton
            onClick={() => onShare('twitter')}
            title={message}
            url={url}
          >
            <TwitterIcon {...iconProps} />
            Twitter
          </TwitterShareButton>
        </ShareMethodButtonOuter>
      )}

      {window?.fbAppId != null &&
        !isTouchDevice &&
        !excludeMethods.includes('messenger') && (
          <ShareMethodButtonOuter>
            <FacebookMessengerShareButton
              appId={fbAppId}
              url={url}
              beforeOnClick={() => {
                onShare('messenger')
              }}
            >
              <MessengerIcon />
              Messenger
            </FacebookMessengerShareButton>
          </ShareMethodButtonOuter>
        )}

      {!excludeMethods.includes('whatsapp') && (
        <ShareMethodButtonOuter>
          <WhatsappShareButton
            onClick={() => onShare('whatsapp')}
            title={message}
            url={url}
          >
            <WhatsappIcon {...iconProps} />
            WhatsApp
          </WhatsappShareButton>
        </ShareMethodButtonOuter>
      )}
    </List>
  )
}

export default ShareList
